import React from 'react';
import { Box, Card, CardContent, Typography, Stack } from '@mui/joy';
import {
  TrendUp as TrendingUpIcon,
  TrendDown as TrendingDownIcon,
  ArrowRight as TrendingFlatIcon,
} from '@phosphor-icons/react';
import type { RankingScanSummary } from '../../../types/marketing';

interface RankingScoreCardProps {
  scan: RankingScanSummary;
  previousScan?: RankingScanSummary | null;
}

const COLORS = {
  top3: '#4CAF50',
  top10: '#FFC107',
  top20: '#FF9800',
  notRanking: '#F44336',
};

export const RankingScoreCard: React.FC<RankingScoreCardProps> = ({ scan, previousScan }) => {
  const scoreDelta = previousScan?.ranking_score != null && scan.ranking_score != null
    ? scan.ranking_score - previousScan.ranking_score
    : null;

  const total = scan.total_points || 1;
  const top3Pct = (scan.points_top3 / total) * 100;
  const top10Pct = ((scan.points_top10 - scan.points_top3) / total) * 100;
  const top20Pct = ((scan.points_top20 - scan.points_top10) / total) * 100;
  const notRankingPct = (scan.points_not_ranking / total) * 100;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Score */}
          <Box sx={{ textAlign: 'center', minWidth: 100 }}>
            <Typography level="body-xs" textTransform="uppercase" fontWeight="lg">
              Keyword Ranking Score
            </Typography>
            <Typography
              level="h1"
              sx={{ fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.2 }}
            >
              {scan.ranking_score != null ? scan.ranking_score.toFixed(1) : '--'}
            </Typography>
            {scoreDelta !== null && (
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                {scoreDelta > 0 ? (
                  <TrendingUpIcon size={16} color="var(--joy-palette-success-500)" />
                ) : scoreDelta < 0 ? (
                  <TrendingDownIcon size={16} color="var(--joy-palette-danger-500)" />
                ) : (
                  <TrendingFlatIcon size={16} color="var(--joy-palette-neutral-500)" />
                )}
                <Typography
                  level="body-xs"
                  color={scoreDelta > 0 ? 'success' : scoreDelta < 0 ? 'danger' : 'neutral'}
                >
                  {scoreDelta > 0 ? '+' : ''}{scoreDelta.toFixed(1)}
                </Typography>
              </Stack>
            )}
          </Box>

          {/* Breakdown bar */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box
              sx={{
                display: 'flex',
                height: 24,
                borderRadius: 'sm',
                overflow: 'hidden',
                mb: 1,
              }}
            >
              {top3Pct > 0 && (
                <Box sx={{ width: `${top3Pct}%`, bgcolor: COLORS.top3 }} />
              )}
              {top10Pct > 0 && (
                <Box sx={{ width: `${top10Pct}%`, bgcolor: COLORS.top10 }} />
              )}
              {top20Pct > 0 && (
                <Box sx={{ width: `${top20Pct}%`, bgcolor: COLORS.top20 }} />
              )}
              {notRankingPct > 0 && (
                <Box sx={{ width: `${notRankingPct}%`, bgcolor: COLORS.notRanking }} />
              )}
            </Box>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <LegendItem color={COLORS.top3} label="Top 3" count={scan.points_top3} total={total} />
              <LegendItem color={COLORS.top10} label="4-10" count={scan.points_top10 - scan.points_top3} total={total} />
              <LegendItem color={COLORS.top20} label="11-20" count={scan.points_top20 - scan.points_top10} total={total} />
              <LegendItem color={COLORS.notRanking} label="Not ranking" count={scan.points_not_ranking} total={total} />
            </Stack>
          </Box>

          {/* Average rank */}
          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography level="body-xs" textTransform="uppercase" fontWeight="lg">
              Avg Rank
            </Typography>
            <Typography level="h3" fontWeight="lg">
              {scan.average_rank != null ? scan.average_rank.toFixed(1) : '--'}
            </Typography>
          </Box>

          {/* Points ranking */}
          <Box sx={{ textAlign: 'center', minWidth: 80 }}>
            <Typography level="body-xs" textTransform="uppercase" fontWeight="lg">
              Ranking
            </Typography>
            <Typography level="h3" fontWeight="lg">
              {scan.points_ranking}/{scan.total_points}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const LegendItem: React.FC<{ color: string; label: string; count: number; total: number }> = ({
  color,
  label,
  count,
  total,
}) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
    <Typography level="body-xs">
      {label} - {count} ({Math.round((count / total) * 100)}%)
    </Typography>
  </Stack>
);
